// Incident creation form — modal content for creating new incidents

import { useState } from 'react'
import type { ServiceStatus } from '@/lib/types-status'

interface IncidentFormProps {
  services: ServiceStatus[]
  onSubmit: (data: {
    title: string
    severity: string
    affectedServices: string[]
    message: string
  }) => void
  isPending: boolean
  onCancel: () => void
}

const SEVERITIES = ['minor', 'major', 'critical'] as const

export function IncidentForm({
  services,
  onSubmit,
  isPending,
  onCancel,
}: IncidentFormProps): React.JSX.Element {
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState<string>('minor')
  const [affected, setAffected] = useState<string[]>([])
  const [message, setMessage] = useState('')

  function toggleService(serviceId: string): void {
    setAffected((prev) =>
      prev.includes(serviceId) ? prev.filter((s) => s !== serviceId) : [...prev, serviceId],
    )
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (!title.trim() || !message.trim() || affected.length === 0) return
    onSubmit({ title: title.trim(), severity, affectedServices: affected, message: message.trim() })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <label className="block text-sm text-text-secondary">
        Title
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Brief description of the incident"
          required
          className="mt-1 block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
        />
      </label>

      {/* Severity */}
      <label className="block text-sm text-text-secondary">
        Severity
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-text-primary focus:border-admin-500 focus:outline-none"
        >
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </label>

      {/* Affected services */}
      <fieldset>
        <legend className="text-sm text-text-secondary">Affected Services</legend>
        <div className="mt-2 space-y-2">
          {services.map((svc) => (
            <label
              key={svc.serviceId}
              className="flex items-center gap-2 text-sm text-text-primary"
            >
              <input
                type="checkbox"
                checked={affected.includes(svc.serviceId)}
                onChange={() => toggleService(svc.serviceId)}
                className="rounded border-surface-3 text-admin-500 focus:ring-admin-500"
              />
              {svc.displayName}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Message */}
      <label className="block text-sm text-text-secondary">
        Initial Message
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Describe what is happening and the current impact..."
          required
          rows={3}
          className="mt-1 block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none resize-none"
        />
      </label>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPending || !title.trim() || !message.trim() || affected.length === 0}
          className="rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600 disabled:opacity-50"
        >
          {isPending ? 'Creating...' : 'Create Incident'}
        </button>
      </div>
    </form>
  )
}
